import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Spinner,
  useToast,
} from '@shared/ui';
import { isApiError } from '@shared/api';
import { LOCALES, taxonomyApi, type Category, type CategoryKind } from '@entities/taxonomy';
import { cn } from '@shared/lib/cn';
import { parentOptions, slugify } from './category-model';

const nameSchema = z.object({
  ru: z.string().min(1, 'Обязательно').max(256),
  en: z.string().min(1, 'Required').max(256),
  uz: z.string().min(1, 'Majburiy').max(256),
});

const createSchema = z.object({
  slug: z
    .string()
    .min(1, 'Обязательно')
    .max(128)
    .regex(/^[a-z0-9-]+$/, 'Только a-z, 0-9 и дефис'),
  name: nameSchema,
  parentId: z.string(),
  sortOrder: z.coerce.number().int().min(0),
});
type CreateValues = z.infer<typeof createSchema>;

const updateSchema = z.object({
  name: nameSchema,
  sortOrder: z.coerce.number().int().min(0),
});
type UpdateValues = z.infer<typeof updateSchema>;

const LOCALE_LABEL: Record<string, string> = { ru: 'RU', en: 'EN', uz: 'UZ' };

/** Minimal shape shared by the create and update forms. */
type NameForm = { name: { ru: string; en: string; uz: string } };

function LocaleNameFields({
  register,
  errors,
}: {
  register: UseFormRegister<NameForm>;
  errors: FieldErrors<NameForm>;
}) {
  const nameErrors = (errors.name ?? {}) as Record<string, { message?: string } | undefined>;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium">Название</span>
        <span className="text-[11px] text-muted-foreground">все три локали обязательны</span>
      </div>
      {LOCALES.map((loc) => {
        const err = nameErrors[loc]?.message;
        return (
          <div key={loc}>
            <div
              className={cn(
                'flex items-center overflow-hidden rounded-md border',
                err ? 'border-destructive' : 'border-input',
              )}
            >
              <span className="w-11 shrink-0 self-stretch border-r bg-muted px-2 py-2 text-center font-mono text-[11px] leading-6 text-muted-foreground">
                {LOCALE_LABEL[loc]}
              </span>
              <input
                {...register(`name.${loc}`)}
                className="h-10 w-full bg-background px-3 text-sm outline-none"
                placeholder={loc === 'ru' ? 'Электроника' : loc === 'en' ? 'Electronics' : 'Elektronika'}
              />
            </div>
            {err ? <p className="mt-0.5 text-xs text-destructive">{err}</p> : null}
          </div>
        );
      })}
    </div>
  );
}

// ---------- Create ----------

export function CreateCategoryDialog({
  open,
  onOpenChange,
  kind,
  parent,
  allOfKind,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  kind: CategoryKind;
  parent: Category | null;
  allOfKind: Category[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const siblings = allOfKind.filter((c) => (c.parentId ?? null) === (parent?.id ?? null));
  const nextSortOrder = siblings.reduce((max, c) => Math.max(max, c.sortOrder + 10), 0);
  const options = parentOptions(allOfKind);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    // Dialog is mounted per open, so defaults are enough — and unlike `values`
    // they never reset the form mid-typing on a parent re-render.
    defaultValues: {
      slug: '',
      name: { ru: '', en: '', uz: '' },
      parentId: parent?.id ?? '',
      sortOrder: nextSortOrder,
    },
  });

  const enName = watch('name.en');

  const create = useMutation({
    mutationFn: (v: CreateValues) =>
      taxonomyApi.createCategory({
        kind,
        slug: v.slug,
        name: v.name,
        parentId: v.parentId || null,
        sortOrder: v.sortOrder,
      }),
    onSuccess: () => {
      toast.success('Категория создана');
      void qc.invalidateQueries({ queryKey: ['categories'] });
      reset();
      onOpenChange(false);
    },
    onError: (e: Error) => {
      const msg = isApiError(e) ? e.message : 'Не удалось создать';
      toast.error('Ошибка', msg);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Новая категория</DialogTitle>
          <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs text-muted-foreground">
            <Badge variant="outline" className="font-mono">
              {kind}
            </Badge>
            <span>›</span>
            <span>{parent ? parent.slug : 'корень'}</span>
          </div>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((v) => create.mutate(v))}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <DialogBody className="space-y-4">
            <LocaleNameFields
              register={register as unknown as UseFormRegister<NameForm>}
              errors={errors as FieldErrors<NameForm>}
            />

            <FormField label="Slug" htmlFor="slug" error={errors.slug?.message}>
              <div className="flex gap-2">
                <Input id="slug" {...register('slug')} placeholder="electronics" className="font-mono" />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!enName}
                  title="Сгенерировать из английского названия"
                  onClick={() => setValue('slug', slugify(enName ?? ''), { shouldValidate: true })}
                >
                  из EN
                </Button>
              </div>
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Родитель" htmlFor="parentId">
                <select
                  id="parentId"
                  {...register('parentId')}
                  className="h-10 w-full rounded-md border border-input bg-background px-2 font-mono text-sm"
                >
                  <option value="">— корень —</option>
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label="Порядок"
                htmlFor="sortOrder"
                error={errors.sortOrder?.message}
                hint="Меньше — выше в списке"
              >
                <Input id="sortOrder" type="number" min={0} {...register('sortOrder')} />
              </FormField>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? <Spinner /> : 'Создать'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Edit ----------

export function EditCategoryDialog({
  open,
  onOpenChange,
  category,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  category: Category | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateValues>({
    resolver: zodResolver(updateSchema),
    values: category
      ? {
          name: {
            ru: category.name?.ru ?? '',
            en: category.name?.en ?? '',
            uz: category.name?.uz ?? '',
          },
          sortOrder: category.sortOrder,
        }
      : undefined,
  });

  const update = useMutation({
    mutationFn: (v: UpdateValues) => taxonomyApi.updateCategory(category!.id, v),
    onSuccess: () => {
      toast.success('Сохранено');
      void qc.invalidateQueries({ queryKey: ['categories'] });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error('Не удалось сохранить', e.message),
  });

  if (!category) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Редактировать категорию</DialogTitle>
          <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs text-muted-foreground">
            <Badge variant="outline" className="font-mono">
              {category.kind}
            </Badge>
            <span className="font-mono">{category.slug}</span>
            <span>· kind и slug изменить нельзя</span>
          </div>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((v) => update.mutate(v))}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <DialogBody className="space-y-4">
            <LocaleNameFields
              register={register as unknown as UseFormRegister<NameForm>}
              errors={errors as FieldErrors<NameForm>}
            />
            <FormField
              label="Порядок"
              htmlFor="edit-sortOrder"
              error={errors.sortOrder?.message}
              hint="Меньше — выше в списке"
            >
              <Input id="edit-sortOrder" type="number" min={0} {...register('sortOrder')} />
            </FormField>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <Spinner /> : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Delete ----------

interface Usage {
  manufacturers?: number;
  products?: number;
  buyRequests?: number;
  hasChildren?: boolean;
}

export function DeleteCategoryDialog({
  category,
  onOpenChange,
}: {
  category: Category | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [blocked, setBlocked] = useState<Usage | null>(null);

  const close = () => {
    setBlocked(null);
    onOpenChange(false);
  };

  const remove = useMutation({
    mutationFn: () => taxonomyApi.deleteCategory(category!.id),
    onSuccess: () => {
      toast.success('Категория удалена');
      void qc.invalidateQueries({ queryKey: ['categories'] });
      close();
    },
    onError: (e: Error) => {
      if (isApiError(e) && e.status === 409) {
        setBlocked((e.problem as unknown as { usage?: Usage }).usage ?? {});
      } else {
        toast.error('Не удалось удалить', e.message);
      }
    },
  });

  if (!category) return null;

  const usageRows = blocked
    ? [
        blocked.hasChildren ? 'есть дочерние категории' : null,
        blocked.manufacturers ? `производителей: ${blocked.manufacturers}` : null,
        blocked.products ? `товаров: ${blocked.products}` : null,
        blocked.buyRequests ? `заявок (RFQ): ${blocked.buyRequests}` : null,
      ].filter(Boolean)
    : [];

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : close())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {blocked ? 'Категорию нельзя удалить' : `Удалить «${category.slug}»?`}
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          {blocked ? (
            <div className="space-y-2 text-sm">
              <p className="text-muted-foreground">На категорию ссылаются:</p>
              <ul className="space-y-1">
                {usageRows.length > 0 ? (
                  usageRows.map((row) => (
                    <li key={row} className="rounded border border-destructive/30 bg-destructive/5 px-3 py-1.5 text-xs">
                      {row}
                    </li>
                  ))
                ) : (
                  <li className="text-xs text-muted-foreground">связанные сущности</li>
                )}
              </ul>
              <p className="text-xs text-muted-foreground">
                Сначала перенесите или удалите связанные объекты — затем повторите.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Действие нельзя отменить. Если на категорию ссылаются товары, заявки или профили
              производителей, сервер откажет в удалении.
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          {blocked ? (
            <Button onClick={close}>Понятно</Button>
          ) : (
            <>
              <Button variant="outline" onClick={close}>
                Отмена
              </Button>
              <Button
                variant="destructive"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                {remove.isPending ? <Spinner /> : 'Удалить'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
